/**
 * MapEngine (CLARID-UX-2026-06, PR 8 — mapping foundation).
 *
 * The one shared Mapbox GL canvas the app standardizes on. Provides the common
 * chrome every map needs — token, base style, navigation + scale controls,
 * graceful "no token" fallback, and error reporting — so pages (PR 9: FL
 * redlining, CRA assessment areas, Bonds, and the central /mapping destination)
 * mount their `<Source>`/`<Layer>` children on a consistent base instead of three
 * divergent maps (two of them Leaflet today). `preserveDrawingBuffer` is on so
 * `useMapExport` can capture the canvas. Foundation only — no page uses it yet.
 */

import { forwardRef } from 'react';
import type { ComponentProps, ReactNode } from 'react';
import Map, { NavigationControl, ScaleControl } from 'react-map-gl/mapbox';
import type { MapRef } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN as string | undefined;

const DEFAULT_VIEW = { longitude: -98.5795, latitude: 39.8283, zoom: 3.5 }; // continental US
const DEFAULT_STYLE = 'mapbox://styles/mapbox/light-v11';

type MapProps = ComponentProps<typeof Map>;

interface MapEngineProps
  extends Pick<
    MapProps,
    'initialViewState' | 'interactiveLayerIds' | 'onMouseMove' | 'onMouseLeave' | 'onClick' | 'onLoad'
  > {
  children?: ReactNode;
  /** Override the base style; defaults to Mapbox Light. */
  mapStyle?: string;
  /** Reported on a Mapbox error (page wires Sentry); logs to console regardless. */
  onMapError?: (error: Error) => void;
  className?: string;
}

const MapEngine = forwardRef<MapRef, MapEngineProps>(function MapEngine(
  { children, mapStyle, onMapError, className, initialViewState, ...handlers },
  ref,
) {
  if (!MAPBOX_TOKEN) {
    return (
      <div
        className={
          className ??
          'flex h-full w-full items-center justify-center rounded-lg border border-gray-200 bg-gray-50 p-8 text-center'
        }
      >
        <p className="max-w-sm text-sm text-gray-500">
          Map unavailable — the Mapbox access token isn’t configured for this environment.
        </p>
      </div>
    );
  }

  return (
    <Map
      ref={ref}
      mapboxAccessToken={MAPBOX_TOKEN}
      initialViewState={initialViewState ?? DEFAULT_VIEW}
      mapStyle={mapStyle ?? DEFAULT_STYLE}
      preserveDrawingBuffer
      style={{ width: '100%', height: '100%' }}
      onError={(evt) => {
        const err =
          (evt as unknown as { error?: Error }).error ?? new Error('Mapbox map error');
        console.error('[MapEngine] Mapbox error:', err);
        onMapError?.(err);
      }}
      {...handlers}
    >
      <NavigationControl position="top-right" />
      <ScaleControl position="bottom-left" />
      {children}
    </Map>
  );
});

export default MapEngine;
